import {
  proStudioUrqlGql as urqlGql,
  SolutionEnvironmentsEnum,
} from '@pypestream/api-services';
import { throwErrorWithId } from '@pypestream/utils';

import {
  checkForErrors,
  getPypeRuleGroups,
  resolveStaleRuleSetIds,
  type RuleSet,
} from '../../utils';
import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';

/**
 * (Re)assigns a channel's pype (per env) to a masking rule set. `addPype…` is an
 * idempotent upsert that does NOT remove the pype from other groups, so a
 * single-select switch has to remove it from wherever it currently sits before
 * adding it to the new one. Either side may be absent (initial assign, or
 * dropping back to the project default).
 *
 * The removals are derived from the groups as they are RIGHT NOW, not from the
 * `fromGroupId` the form captured when it loaded — see `resolveStaleRuleSetIds`
 * for why (a coworker's reassignment in between would otherwise leave the pype
 * in two groups at once).
 */
export const assignPypeRuleGroup = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<{ pypeRuleGroups: RuleSet[]; cb?: () => void } | void> => {
  if (event.type !== 'assignPypeRuleGroup') {
    return;
  }

  const projectId = ctx.project?.id;

  if (!projectId) {
    throwErrorWithId('Project ID is required');
    return;
  }

  const { pypeId, env, fromGroupId, toGroupId, cb } = event.payload;
  const solutionEnv =
    env === 'live'
      ? SolutionEnvironmentsEnum.Live
      : SolutionEnvironmentsEnum.Sandbox;

  const staleGroupIds = resolveStaleRuleSetIds({
    ruleSets: await getPypeRuleGroups({ projectId }),
    pypeId,
    env,
    toGroupId,
    fromGroupId,
  });

  for (const id of staleGroupIds) {
    const removeResponse = await urqlGql.removePypeFromPypeRuleGroup({
      id,
      pypeId,
      env: solutionEnv,
    });
    checkForErrors(removeResponse);
  }

  if (toGroupId) {
    const addResponse = await urqlGql.addPypeToPypeRuleGroup({
      id: toGroupId,
      pypeId,
      env: solutionEnv,
    });
    checkForErrors(addResponse);
  }

  const pypeRuleGroups = await getPypeRuleGroups({ projectId });

  return { pypeRuleGroups, cb };
};
